import { useEffect, useState } from 'react';
import { adminApi, AdminUser } from '../../shared/api/admin.api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { MoreVertical, Pencil, Plus } from 'lucide-react';
import { confirmDialog } from '../../shared/ui/ConfirmHost';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner@2.0.3';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';

const WORK_ROLE: Record<string, string> = {
  head: 'Руководитель',
  content_maker: 'Контент-мейкер',
  editor: 'Монтажёр',
  moderator: 'Модератор',
  distributor: 'Дистрибьютор',
};

export function UsersPage() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState<'admin' | 'employee'>('employee');
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<AdminUser | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);

  async function load() {
    try {
      const { users } = await adminApi.listUsers();
      setUsers(users);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true);
    try {
      await adminApi.inviteUser({ username: newUsername, password: newPassword, full_name: newName, role: newRole });
      toast.success(`Сотрудник ${newUsername} добавлен`);
      setCreateOpen(false);
      setNewUsername(''); setNewPassword(''); setNewName('');
      load();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setCreating(false);
    }
  }

  async function handleRoleChange(userId: string, role: string) {
    const user = users.find((u) => u.id === userId);
    if (!user || user.role === role) return;
    const who = user.full_name ?? user.username ?? 'сотрудник';
    // Права администратора — не то, что меняют случайным кликом по списку.
    const ok = await confirmDialog(role === 'admin'
      ? {
          title: `Сделать «${who}» администратором?`,
          description: 'Администратор видит все аккаунты, добавляет сотрудников и раздаёт доступы.',
          confirmText: 'Сделать администратором',
        }
      : {
          title: `Снять права администратора у «${who}»?`,
          description: 'Останутся только аккаунты, выданные на вкладке «Доступы к аккаунтам».',
          confirmText: 'Снять права',
          destructive: true,
        });
    if (!ok) return;
    try {
      await adminApi.updateUser(userId, { role });
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, role: role as any } : u));
      toast.success('Роль обновлена');
    } catch (e: any) {
      toast.error(e.message);
    }
  }

  function startRename(user: AdminUser) {
    setRenaming(user);
    setRenameValue(user.full_name ?? '');
  }

  async function handleRename(e: React.FormEvent) {
    e.preventDefault();
    if (!renaming) return;
    const full_name = renameValue.trim();
    setRenameSaving(true);
    try {
      await adminApi.updateUser(renaming.id, { full_name });
      setUsers((prev) => prev.map((u) => u.id === renaming.id ? { ...u, full_name } : u));
      toast.success('Имя сохранено');
      setRenaming(null);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setRenameSaving(false);
    }
  }

  if (loading) return <p className="text-muted-foreground">Загрузка...</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button size="sm"><Plus />Добавить сотрудника</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Добавить сотрудника</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-2">
              <div className="space-y-1">
                <Label>Логин</Label>
                <Input
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  type="text"
                  autoComplete="off"
                  required
                />
              </div>
              <div className="space-y-1">
                <Label>Пароль</Label>
                <Input
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  type="password"
                  autoComplete="new-password"
                  required
                />
              </div>
              <div className="space-y-1">
                <Label>Имя</Label>
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Роль</Label>
                <Select value={newRole} onValueChange={(v) => setNewRole(v as any)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">Сотрудник</SelectItem>
                    <SelectItem value="admin">Админ</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full" disabled={creating}>
                {creating ? 'Добавляем…' : 'Добавить сотрудника'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-3">Имя</th>
              <th className="text-left p-3">Логин</th>
              {/* Назначения меняет руководитель в своём «Отделении» — у админа экрана для них нет. */}
              <th className="text-left p-3" title="Меняет руководитель отделения в разделе «Отделение»">Отделения и назначения</th>
              <th className="text-left p-3">Доступ</th>
              <th className="p-3 w-10" />
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className="border-t">
                <td className="p-3">
                  <span className="flex items-center gap-2.5">
                    <Avatar className="size-8">
                      <AvatarImage src={user.avatar_url ?? undefined} alt="" />
                      <AvatarFallback className="text-xs">{(user.full_name ?? user.username ?? '?').slice(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    {user.full_name ?? '—'}
                  </span>
                </td>
                <td className="p-3">{user.username ?? '—'}</td>
                <td className="p-3">
                  {(user.assignments ?? []).length > 0 ? (
                    <div className="flex flex-col gap-2">
                      {(user.assignments ?? []).map((assignment) => (
                        <div key={assignment.divisionId} className="flex flex-col gap-1">
                          <span className="font-medium">{assignment.divisionName} · {WORK_ROLE[assignment.role] ?? assignment.role}</span>
                          {assignment.directions.length > 0 && (
                            <span className="flex flex-wrap gap-1">
                              {assignment.directions.map((direction) => (
                                <span key={`${direction.id}:${direction.role}`} className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                                  {direction.name} · {WORK_ROLE[direction.role] ?? direction.role}
                                </span>
                              ))}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : <span className="text-muted-foreground">Не назначен</span>}
                </td>
                <td className="p-3">
                  <Select value={user.role} onValueChange={(v) => handleRoleChange(user.id, v)}>
                    <SelectTrigger className="w-32 h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee">Сотрудник</SelectItem>
                      <SelectItem value="admin">Админ</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-3 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Действия с сотрудником">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => startRename(user)}>
                        <Pencil className="h-4 w-4" />
                        Переименовать
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={renaming !== null} onOpenChange={(open) => { if (!open && !renameSaving) setRenaming(null); }}>
        <DialogContent className="sm:max-w-sm">
          <form onSubmit={handleRename} className="space-y-4">
            <DialogHeader>
              <DialogTitle>Переименовать</DialogTitle>
            </DialogHeader>
            <div className="space-y-1">
              <Label htmlFor="rename-full-name">Имя</Label>
              <Input id="rename-full-name" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} required autoFocus />
              {renaming?.username && <p className="text-xs text-muted-foreground">Логин @{renaming.username} не меняется.</p>}
            </div>
            <DialogFooter>
              <Button type="submit" disabled={renameSaving || !renameValue.trim()}>
                {renameSaving ? 'Сохраняем…' : 'Сохранить'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
